import { useParams } from "react-router-dom"
import { MoneyFlowDiagram } from "../sankey/MoneyFlowDiagram"
import { useContext, useEffect, useRef } from "react"
import { MoneyFlowContext } from "../../context/MoneyFlowContext"
import MoneyFlowLogo from '../../assets/MoneyFlow_logo.svg?react'
import { DiagramViewPortProvider } from "../sankey/DiagramViewPortProvider"
import { DiagramActions } from "../sankey/actions/DiagramActions"
import { ConfigProvider, Flex, Tag, Divider, Typography, Button, Layout, message } from "antd"
import { useCloneMoneyFlowMutation } from "../../api/moneyflow/clone"
import { DiagramToolbar } from "../sankey/toolbar/DiagramToolbar"
import { MoonOutlined, SunOutlined } from "@ant-design/icons"
import { ShareableLink } from "../../backend/types/shareableLink"
import { getAntdTheme } from "../../theme/antdTheme.ts"
import { useDarkMode } from "../../hooks/useDarkMode.tsx"

export function PublicMoneyflowPage() {
    const { moneyFlow } = useContext(MoneyFlowContext)
    // Same theme store as the rest of the app (and the cookie banner), not a page-local default
    const { darkModeEnabled: darkMode, switchDarkMode: toggleDarkMode } = useDarkMode()
    const { originalId } = useParams()
    const { mutate: cloneMoneyFlow, isPending } = useCloneMoneyFlowMutation(originalId!)
    const mfWrapper = useRef<HTMLDivElement>(null)
    const [messageApi, contextHolder] = message.useMessage()

    const { network, content, createdAt } = moneyFlow || {}
    const address = content?.addresses[0] || ''
    const publicLink = moneyFlow?.publicLink as unknown as ShareableLink | undefined

    const handleUserClick = (e: Event) => {
        e.preventDefault()
        e.stopPropagation()

        return false
    }

    const handleForkMoneyFlow = () => {
        cloneMoneyFlow()
        messageApi.success(
            'MoneyFlow is being created. You will be redirected to the new MoneyFlow page once it is ready.'
        )
    }

    useEffect(() => {
        const mfWrapperElement = mfWrapper.current
        mfWrapperElement?.addEventListener('click', handleUserClick, true)
        return () => {
            mfWrapperElement?.removeEventListener('click', handleUserClick, true)
        }
    }, [])

    return (
        <ConfigProvider theme={getAntdTheme(darkMode)}>
            <DiagramViewPortProvider>
                <Layout style={{ height: '100vh', width: '100%', padding: '12px' }}>
                    <Flex justify="space-between" align="center" >

                        <a style={{ display: 'inline-block', width: '300px' }} href="https://lite.bitquery.io/" title="MoneyFlow Lite"><MoneyFlowLogo width={189} height={60} style={{ color: 'var(--ant-color-text)' }} /></a>
                        <Button
                            title={darkMode ? 'Light Mode' : 'Dark Mode'}
                            icon={darkMode ? <MoonOutlined /> : <SunOutlined />}
                            onClick={toggleDarkMode}
                        />
                    </Flex>
                    <Flex justify="space-between" align="end">
                        <div>
                            <Typography.Title style={{ marginTop: 0 }} level={4}>{publicLink?.name}</Typography.Title>
                            <Flex gap={8}>
                                <Tag>  <strong>Address:</strong> {address}</Tag>
                                <Tag>  <strong>Blockchain:</strong> {network || 'Unknown'}</Tag>
                                {content?.dateRange[0] && content?.dateRange[1] && (
                                    <Tag>
                                        <strong>Date Range:</strong> {new Date(content.dateRange[0]).toLocaleDateString()} - {new Date(content.dateRange[1]).toLocaleDateString()}
                                    </Tag>
                                )}
                                {createdAt && <Tag><strong>Generated:</strong> {new Date(createdAt).toLocaleDateString()}</Tag>}
                            </Flex>
                        </div>
                        <Button size="large" type="primary" onClick={handleForkMoneyFlow} loading={isPending}>
                            Analyze this Money Flow
                        </Button>
                    </Flex>

                    <Divider style={{ margin: '10px 0' }} />
                    <div style={{ width: '100%', height: 'calc(100% - 140px)' }} >
                        <DiagramActions />
                        <DiagramToolbar />
                        <div style={{ width: '100%', height: '100%' }} ref={mfWrapper}>
                            <MoneyFlowDiagram />
                        </div >
                    </div>
                    {contextHolder}
                </Layout>
            </DiagramViewPortProvider>
        </ConfigProvider>
    )
}
